import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import {
  MapPin,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Copy,
  Play,
  Pause,
  Settings,
  BarChart3,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Zap,
  Target,
  GitBranch,
  ArrowRight,
  Download,
  Upload,
  TestTube,
  Activity,
  Brain,
  Sparkles,
  Mail,
  Eye,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { sanitizeEmailContent } from "@/lib/sanitize";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/hooks/useAuthState";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  insertUserJourneyTemplateSchema,
  insertCustomJourneySchema,
  type InsertUserJourneyTemplate,
  type InsertCustomJourney,
  type UserJourneyTemplate,
  type CustomJourney as SharedCustomJourney,
} from "@shared/schema";
import StepConfiguration from "@/components/journey/StepConfiguration";
import JourneyTesting from "@/components/journey/JourneyTesting";
import { formatDatePretty, formatDateOnlySAST } from "@/lib/dateUtils";

// Use base shared schemas with UI-specific extensions for form validation
const templateFormSchema = z.object({
  name: z.string().min(1, "Template name is required"),
  description: z.string().min(1, "Description is required"),
  category: z.string().min(1, "Category is required"),
  triggerEvent: z.string().min(1, "Trigger event is required"),
  templateType: z.string().default("custom"),
  status: z.string().default("draft"),
  isPublic: z.boolean().default(false),
  isSystemTemplate: z.boolean().default(false),
  version: z.string().default("1.0"),
  // UI-specific fields for backward compatibility
  isActive: z.boolean().default(true),
  config: z
    .object({
      maxSteps: z.number().min(1).max(20).default(5),
      defaultDelay: z.number().min(0).default(24),
      priority: z.enum(["low", "medium", "high"]).default("medium"),
    })
    .optional(),
});

const journeyFormSchema = z.object({
  name: z.string().min(1, "Journey name is required"),
  description: z.string().min(1, "Description is required"),
  triggerEvent: z.string().min(1, "Trigger event is required"),
  channel: z.enum(["email", "sms", "in_app"]).default("email"),
  templateId: z.number().optional(),
  targetSegmentId: z.number().optional(),
  sesTemplateName: z.string().optional(), // AWS SES template name
  journeyType: z.string().default("standalone"),
  status: z.string().default("draft"),
  isTemplate: z.boolean().default(false),
  version: z.string().default("1.0"),
  // UI-specific fields for backward compatibility
  isActive: z.boolean().default(false),
  config: z
    .object({
      maxSteps: z.number().min(1).max(20).default(5),
      throttleLimit: z.number().min(1).default(25000),
      allowWeekends: z.boolean().default(true),
      smsTemplateId: z.number().optional(), // For SMS journeys
    })
    .optional(),
});

type TemplateForm = z.infer<typeof templateFormSchema>;
type JourneyForm = z.infer<typeof journeyFormSchema>;

// Use shared types with extensions for API response data
type JourneyTemplate = UserJourneyTemplate & {
  usage_count?: number;
  // Add UI compatibility fields
  isActive?: boolean;
  config?: any;
};

type CustomJourney = SharedCustomJourney & {
  stepCount?: number;
  activeUsers?: number;
  totalSent?: number;
  conversionRate?: number;
  templateName?: string;
  segmentName?: string;
  // Add UI compatibility fields
  isActive?: boolean;
  config?: any;
};

interface TriggerEvent {
  id: string;
  name: string;
  description: string;
  category: string;
  parameters?: string[];
}

export default function JourneyBuilder() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("journeys");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  // Dialog states
  const [isCreateTemplateDialogOpen, setIsCreateTemplateDialogOpen] =
    useState(false);
  const [isEditTemplateDialogOpen, setIsEditTemplateDialogOpen] =
    useState(false);
  const [isCreateJourneyDialogOpen, setIsCreateJourneyDialogOpen] =
    useState(false);
  const [isEditJourneyDialogOpen, setIsEditJourneyDialogOpen] = useState(false);
  const [isAnalyticsDialogOpen, setIsAnalyticsDialogOpen] = useState(false);
  const [isTestJourneyDialogOpen, setIsTestJourneyDialogOpen] = useState(false); // State for the new test journey dialog

  // Selected items
  const [selectedTemplate, setSelectedTemplate] =
    useState<JourneyTemplate | null>(null);
  const [selectedJourney, setSelectedJourney] = useState<CustomJourney | null>(
    null
  );
  const [selectedJourneyForConfigure, setSelectedJourneyForConfigure] =
    useState<CustomJourney | null>(null);
  const [selectedJourneyForTest, setSelectedJourneyForTest] =
    useState<CustomJourney | null>(null); // State for the journey to be tested

  // Test journey dialog state
  const [testEmail, setTestEmail] = useState("");
  const [testData, setTestData] = useState({});

  // Pagination
  const [templatePage, setTemplatePage] = useState(1);
  const [journeyPage, setJourneyPage] = useState(1);

  // Journey delivery reports state (Templates tab repurposed as reports)
  const [selectedReportJourneyId, setSelectedReportJourneyId] =
    useState<string>("all");

  // Template preview state for journeys (AWS SES)
  const [isTemplatePreviewOpen, setIsTemplatePreviewOpen] = useState(false);
  const [templatePreviewData, setTemplatePreviewData] = useState<any | null>(
    null
  );
  const [templatePreviewJourney, setTemplatePreviewJourney] =
    useState<CustomJourney | null>(null);

  const previewJourneyTemplateMutation = useMutation({
    mutationFn: async ({
      templateName,
      variables,
    }: {
      templateName: string;
      variables: Record<string, string>;
    }) => {
      const response = await apiRequest(
        "POST",
        `/api/admin/crm/ses-templates/${encodeURIComponent(
          templateName
        )}/preview`,
        { variables }
      );
      return response.json();
    },
    onSuccess: (data) => {
      setTemplatePreviewData(data);
      setIsTemplatePreviewOpen(true);
    },
    onError: (error: any) => {
      toast({
        title: "Error previewing template",
        description:
          error.message || "Failed to preview AWS SES template for journey",
        variant: "destructive",
      });
    },
  });

  // Fetch journey templates
  const {
    data: templatesData,
    isLoading: templatesLoading,
    refetch: refetchTemplates,
  } = useQuery({
    queryKey: [
      "/api/admin/journey-builder/templates",
      templatePage,
      selectedCategory,
      searchTerm,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: templatePage.toString(),
        limit: "12",
      });
      if (selectedCategory !== "all")
        params.append("category", selectedCategory);
      if (searchTerm) params.append("search", searchTerm);

      const response = await apiRequest(
        "GET",
        `/api/admin/journey-builder/templates?${params}`
      );
      return response.json();
    },
  });

  // Fetch custom journeys
  const {
    data: journeysData,
    isLoading: journeysLoading,
    refetch: refetchJourneys,
  } = useQuery({
    queryKey: [
      "/api/admin/journey-builder/journeys",
      journeyPage,
      selectedStatus,
      searchTerm,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: journeyPage.toString(),
        limit: "20",
      });
      if (selectedStatus !== "all") params.append("status", selectedStatus);
      if (searchTerm) params.append("search", searchTerm);

      const response = await apiRequest(
        "GET",
        `/api/admin/journey-builder/journeys?${params}`
      );
      return response.json();
    },
  });

  // Fetch trigger events
  const { data: triggerEvents } = useQuery({
    queryKey: ["/api/admin/journey-builder/trigger-events"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        "/api/admin/journey-builder/trigger-events"
      );
      return response.json();
    },
  });

  // Fetch user segments
  const { data: segments } = useQuery({
    queryKey: ["/api/segments"],
  });

  // Fetch AWS SES templates
  const { data: sesTemplatesData } = useQuery({
    queryKey: ["/api/admin/crm/ses-templates"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/crm/ses-templates");
      return response.json();
    },
  });

  const templates = templatesData?.templates || [];
  const sesTemplates = sesTemplatesData?.templates || [];
  const templatesPagination = templatesData?.pagination || {};
  const journeys = journeysData?.data || [];
  const journeysPagination = journeysData?.pagination || {};

  // Fetch SMS templates for SMS journeys
  const { data: smsTemplatesData } = useQuery<any[]>({
    queryKey: ["/api/sms-config/templates"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/sms-config/templates");
      return response.json();
    },
  });
  const smsTemplates = smsTemplatesData || [];

  // Fetch email delivery reports for a specific journey (when selected in Templates tab)
  const { data: journeyDeliveryData, isLoading: journeyDeliveryLoading } =
    useQuery({
      queryKey: [
        "/api/admin/journey-builder/journeys/email-delivery",
        selectedReportJourneyId,
      ],
      queryFn: async () => {
        if (selectedReportJourneyId === "all") return null;
        const response = await apiRequest(
          "GET",
          `/api/admin/journey-builder/journeys/${selectedReportJourneyId}/email-delivery`
        );
        return response.json();
      },
      enabled: selectedReportJourneyId !== "all",
    });

  // Forms with schema-compatible defaults
  const templateForm = useForm<TemplateForm>({
    resolver: zodResolver(templateFormSchema),
    defaultValues: {
      name: "",
      description: "",
      category: "general",
      triggerEvent: "",
      templateType: "custom",
      status: "draft",
      isPublic: false,
      isSystemTemplate: false,
      version: "1.0",
      isActive: true,
      config: {
        maxSteps: 5,
        defaultDelay: 24,
        priority: "medium",
      },
    },
  });

  const journeyForm = useForm<JourneyForm>({
    resolver: zodResolver(journeyFormSchema),
    defaultValues: {
      name: "",
      description: "",
      triggerEvent: "",
      channel: "email",
      sesTemplateName: "",
      journeyType: "standalone",
      status: "draft",
      isTemplate: false,
      version: "1.0",
      isActive: false,
      config: {
        maxSteps: 5,
        throttleLimit: 25000,
        allowWeekends: true,
      },
    },
  });

  const selectedJourneyChannel = journeyForm.watch("channel");

  // Calculate health metrics: journeys with send failures in last 24 hours.
  const journeysWithRecentErrors = journeys.filter(
    (j) => (j as any).metrics?.recentSendFailures24h > 0
  ).length;

  // Mutations
  const createTemplateMutation = useMutation({
    mutationFn: async (data: TemplateForm) => {
      if (!user) {
        throw new Error("User not authenticated");
      }

      const response = await apiRequest(
        "POST",
        "/api/admin/journey-builder/templates",
        {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...data,
            createdBy: parseInt(user.id),
          }),
        }
      );
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Template created successfully!" });
      setIsCreateTemplateDialogOpen(false);
      templateForm.reset();
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/journey-builder/templates"],
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error creating template",
        description: error.message || "Failed to create template",
        variant: "destructive",
      });
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: async (data: TemplateForm & { id: number }) => {
      if (!user) {
        throw new Error("User not authenticated");
      }

      const response = await apiRequest(
        "PUT",
        `/api/admin/journey-builder/templates/${data.id}`,
        {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...data,
            lastModifiedBy: parseInt(user.id),
          }),
        }
      );
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Template updated successfully!" });
      setIsEditTemplateDialogOpen(false);
      templateForm.reset();
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/journey-builder/templates"],
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating template",
        description: error.message || "Failed to update template",
        variant: "destructive",
      });
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (templateId: number) => {
      const response = await apiRequest(
        "DELETE",
        `/api/admin/journey-builder/templates/${templateId}`
      );
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Template deleted successfully!" });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/journey-builder/templates"],
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting template",
        description: error.message || "Failed to delete template",
        variant: "destructive",
      });
    },
  });

  const duplicateTemplateMutation = useMutation({
    mutationFn: async (templateId: number) => {
      const response = await apiRequest(
        "POST",
        `/api/admin/journey-builder/templates/${templateId}/duplicate`
      );
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Template duplicated successfully!" });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/journey-builder/templates"],
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error duplicating template",
        description: error.message || "Failed to duplicate template",
        variant: "destructive",
      });
    },
  });

  const createJourneyMutation = useMutation({
    mutationFn: async (data: JourneyForm) => {
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Transform config into schedulingSettings
      const payload = {
        name: data.name,
        description: data.description,
        triggerEvent: data.triggerEvent,
        templateId: data.templateId,
        targetSegmentId: data.targetSegmentId,
        sesTemplateName: data.sesTemplateName || undefined,
        status: data.isActive ? "active" : "draft",
        schedulingSettings: {
          rateLimit: data.config?.throttleLimit || 25000,
          sendOnWeekends: data.config?.allowWeekends !== false,
        },
        settings: {
          deliveryChannel: data.channel,
          channel: data.channel,
          // Persist initial SMS template choice so immediate sends work
          smsTemplateId: data.config?.smsTemplateId
            ? Number(data.config.smsTemplateId)
            : undefined,
        },
        createdBy: parseInt(user.id),
      };

      const response = await apiRequest(
        "POST",
        "/api/admin/journey-builder/journeys",
        {
          body: payload,
        }
      );
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Journey created successfully!" });
      setIsCreateJourneyDialogOpen(false);
      journeyForm.reset();
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/journey-builder/journeys"],
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error creating journey",
        description: error.message || "Failed to create journey",
        variant: "destructive",
      });
    },
  });

  const updateJourneyMutation = useMutation({
    mutationFn: async (data: JourneyForm & { id: number }) => {
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Mirror the create payload shape, but without createdBy / lastModifiedBy,
      // to avoid foreign-key issues on custom_journeys.last_modified_by.
      const payload = {
        name: data.name,
        description: data.description,
        triggerEvent: data.triggerEvent,
        templateId: data.templateId,
        targetSegmentId: data.targetSegmentId,
        sesTemplateName: data.sesTemplateName || undefined,
        status: data.isActive ? "active" : "draft",
        schedulingSettings: {
          rateLimit: data.config?.throttleLimit || 25000,
          sendOnWeekends: data.config?.allowWeekends !== false,
        },
        settings: {
          deliveryChannel: data.channel,
          channel: data.channel,
          smsTemplateId: data.config?.smsTemplateId
            ? Number(data.config.smsTemplateId)
            : undefined,
        },
      };

      const response = await apiRequest(
        "PUT",
        `/api/admin/journey-builder/journeys/${data.id}`,
        {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Journey updated successfully!" });
      setIsEditJourneyDialogOpen(false);
      journeyForm.reset();
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/journey-builder/journeys"],
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating journey",
        description: error.message || "Failed to update journey",
        variant: "destructive",
      });
    },
  });

  const toggleJourneyMutation = useMutation({
    mutationFn: async ({
      journeyId,
      isActive,
    }: {
      journeyId: number;
      isActive: boolean;
    }) => {
      const response = await apiRequest(
        "POST",
        `/api/admin/journey-builder/journeys/${journeyId}/activate`,
        {
          headers: { "Content-Type": "application/json" },
          body: { isActive },
        }
      );
      return response.json();
    },
    onSuccess: (data, variables) => {
      toast({
        title: `Journey ${
          variables.isActive ? "activated" : "deactivated"
        } successfully!`,
        description: data.message || "Journey status updated",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/journey-builder/journeys"],
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error toggling journey",
        description: error.message || "Failed to toggle journey status",
        variant: "destructive",
      });
    },
  });

  const deleteCustomJourneyMutation = useMutation({
    mutationFn: async (journeyId: number) => {
      const response = await apiRequest(
        "DELETE",
        `/api/admin/journey-builder/journeys/${journeyId}`
      );
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Journey deleted",
        description: data?.message || "Custom journey removed",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/journey-builder/journeys"],
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting journey",
        description: error.message || "Failed to delete custom journey",
        variant: "destructive",
      });
    },
  });

  const handleDeleteJourney = (journey: CustomJourney) => {
    if (deleteCustomJourneyMutation.isPending) {
      return;
    }

    const confirmation = window.confirm(
      `Permanently delete the journey "${journey.name}"? This action cannot be undone.`
    );

    if (!confirmation) {
      return;
    }

    deleteCustomJourneyMutation.mutate(journey.id);
  };

  const testJourneyMutation = useMutation({
    mutationFn: async ({
      journeyId,
      testEmail,
      testData,
    }: {
      journeyId: number;
      testEmail: string;
      testData: any;
    }) => {
      const response = await apiRequest(
        "POST",
        `/api/admin/journey-builder/journeys/${journeyId}/test`,
        {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: testEmail, data: testData }),
        }
      );
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Journey test initiated!",
        description:
          data.message || "Test emails sent successfully. Check your inbox.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error testing journey",
        description: error.message || "Failed to test journey",
        variant: "destructive",
      });
    },
  });

  // Event handlers
  const handleEditTemplate = (template: JourneyTemplate) => {
    setSelectedTemplate(template);
    templateForm.reset({
      name: template.name,
      description: template.description,
      category: template.category,
      triggerEvent: template.triggerEvent,
      isActive: template.isActive,
      config: template.config || {
        maxSteps: 5,
        defaultDelay: 24,
        priority: "medium",
      },
    });
    setIsEditTemplateDialogOpen(true);
  };

  const handleEditJourney = (journey: CustomJourney) => {
    setSelectedJourney(journey);
    journeyForm.reset({
      name: journey.name,
      description: journey.description,
      channel: (journey as any).settings?.deliveryChannel || "email",
      templateId: journey.templateId,
      triggerEvent: journey.triggerEvent,
      targetSegmentId: journey.targetSegmentId,
      sesTemplateName: journey.sesTemplateName,
      journeyType: journey.journeyType || "standalone",
      status: journey.status || "draft",
      isTemplate: journey.isTemplate || false,
      version: journey.version || "1.0",
      isActive: journey.status === "active" || journey.isActive,
      config: {
        maxSteps: journey.config?.maxSteps ?? 5,
        throttleLimit:
          (journey.schedulingSettings as any)?.rateLimit ??
          journey.config?.throttleLimit ??
          100,
        allowWeekends:
          (journey.schedulingSettings as any)?.sendOnWeekends ??
          journey.config?.allowWeekends ??
          true,
        smsTemplateId:
          (journey.settings as any)?.smsTemplateId ??
          (journey.config as any)?.smsTemplateId ??
          undefined,
      },
    });
    setIsEditJourneyDialogOpen(true);
  };

  const handleViewAnalytics = (journey: CustomJourney) => {
    setSelectedJourney(journey);
    setIsAnalyticsDialogOpen(true);
  };

  const handleTestJourney = (journey: CustomJourney) => {
    setSelectedJourneyForTest(journey);
    setIsTestJourneyDialogOpen(true);
  };

  // Categories for templates
  const templateCategories = [
    { value: "all", label: "All Categories" },
    { value: "general", label: "General" },
    { value: "welcome", label: "Welcome" },
    { value: "onboarding", label: "Onboarding" },
    { value: "engagement", label: "Engagement" },
    { value: "tier_advancement", label: "Tier Advancement" },
    { value: "share_verification", label: "Share Verification" },
    { value: "promotional", label: "Promotional" },
    { value: "retention", label: "Retention" },
  ];

  // Status options for journeys
  const journeyStatuses = [
    { value: "all", label: "All Statuses" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "draft", label: "Draft" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <MapPin className="h-8 w-8 text-blue-600" />
              User Journeys
            </h1>
            <p className="text-gray-600 mt-2">
              Create and manage standalone automated journeys for user engagement
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              data-testid="button-export-journeys"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="button-import-templates"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import
            </Button>
            <Button
              onClick={() => {
                // Reset state for creating a new journey
                setSelectedJourney(null);
                journeyForm.reset({
                  name: "",
                  description: "",
                  triggerEvent: "",
                  channel: "email",
                  templateId: undefined,
                  targetSegmentId: undefined,
                  sesTemplateName: "",
                  journeyType: "standalone",
                  status: "draft",
                  isTemplate: false,
                  version: "1.0",
                  isActive: false,
                  config: {
                    maxSteps: 5,
                    throttleLimit: 25000,
                    allowWeekends: true,
                  },
                });
                setIsCreateJourneyDialogOpen(true);
                setIsEditJourneyDialogOpen(false);
              }}
              data-testid="button-create-new"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Journey
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Active Journeys
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {journeys.filter(
                    (j) => j.status === "active" || j.isActive
                  ).length}
                </p>
              </div>
              <Activity className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Avg. Conversion
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {journeys.length > 0
                    ? `${Math.round(
                        journeys.reduce(
                          (sum, j) => sum + (j.conversionRate || 0),
                          0
                        ) / journeys.length
                      )}%`
                    : "0%"}
                </p>
              </div>
              <Target className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Journeys with send errors (24h)
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {journeysWithRecentErrors}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  id="search"
                  placeholder="Search by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="input-search"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="category-filter">
                {activeTab === "templates" ? "Category" : "Status"}
              </Label>
              <Select
                value={
                  activeTab === "templates" ? selectedCategory : selectedStatus
                }
                onValueChange={
                  activeTab === "templates"
                    ? setSelectedCategory
                    : setSelectedStatus
                }
              >
                <SelectTrigger data-testid="select-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(activeTab === "templates"
                    ? templateCategories
                    : journeyStatuses
                  ).map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                size="sm"
                data-testid="button-advanced-filters"
              >
                <Filter className="h-4 w-4 mr-2" />
                More Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger
            value="journeys"
            className="flex items-center gap-2"
            data-testid="tab-journeys"
          >
            <MapPin className="h-4 w-4" />
            Journeys
          </TabsTrigger>
          <TabsTrigger
            value="configure"
            className="flex items-center gap-2"
            data-testid="tab-configure"
          >
            <GitBranch className="h-4 w-4" />
            Configure
          </TabsTrigger>
          <TabsTrigger
            value="testing"
            className="flex items-center gap-2"
            data-testid="tab-testing"
          >
            <TestTube className="h-4 w-4" />
            Testing
          </TabsTrigger>
        </TabsList>

        {/* Journeys Tab */}
        <TabsContent value="journeys" className="space-y-6">
          {journeysLoading ? (
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-gray-500">Loading journeys...</p>
                </div>
              </CardContent>
            </Card>
          ) : journeys.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <MapPin className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No journeys found
                </h3>
                <p className="text-gray-500 mb-6">
                  {searchTerm || selectedStatus !== "all"
                    ? "No journeys match your current filters."
                    : "Get started by creating your first custom journey."}
                </p>
                {!searchTerm && selectedStatus === "all" && (
                  <Button
                    onClick={() => setIsCreateJourneyDialogOpen(true)}
                    data-testid="button-create-first-journey"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Journey
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Custom Journeys ({journeys.length})</CardTitle>
                  <CardDescription>
                    Manage your custom email journeys and monitor their
                    performance
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Journey</TableHead>
                          <TableHead>AWS SES Template</TableHead>
                          <TableHead>Trigger Event</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Rate Limiting</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {journeys.map((journey: CustomJourney) => (
                          <TableRow
                            key={journey.id}
                            data-testid={`journey-row-${journey.id}`}
                          >
                            <TableCell>
                              <div>
                                <div className="font-medium">
                                  {journey.name}
                                </div>
                                <div className="text-sm text-gray-500 line-clamp-1">
                                  {journey.description}
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    Immediate send
                                  </Badge>
                                  {journey.targetSegmentId && (
                                    <Badge
                                      variant="secondary"
                                      className="text-xs"
                                    >
                                      Segment {journey.targetSegmentId}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {journey.sesTemplateName ? (
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary">
                                    {journey.sesTemplateName}
                                  </Badge>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => {
                                      setTemplatePreviewJourney(journey);
                                      previewJourneyTemplateMutation.mutate({
                                        templateName: journey.sesTemplateName!,
                                        variables: {},
                                      });
                                    }}
                                    data-testid={`button-preview-journey-template-${journey.id}`}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">
                                  No template
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Zap className="h-4 w-4 text-yellow-500" />
                                <span className="text-sm">
                                  {triggerEvents?.find(
                                    (e: TriggerEvent) =>
                                      e.id === journey.triggerEvent
                                  )?.name || journey.triggerEvent}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant={
                                    journey.status === "active"
                                      ? "default"
                                      : journey.status === "draft"
                                      ? "secondary"
                                      : journey.status === "paused"
                                      ? "outline"
                                      : "secondary"
                                  }
                                >
                                  {journey.status
                                    ? journey.status.charAt(0).toUpperCase() +
                                      journey.status.slice(1)
                                    : "Draft"}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {journey.schedulingSettings &&
                                typeof journey.schedulingSettings ===
                                  "object" ? (
                                  <div className="text-xs space-y-1">
                                    {(journey.schedulingSettings as any)
                                      .rateLimit && (
                                      <div className="flex items-center gap-1">
                                        <Clock className="h-3 w-3 text-gray-500" />
                                        <span>
                                          {(journey.schedulingSettings as any)
                                            .rateLimit.maxPerHour || "N/A"}
                                          /hr
                                        </span>
                                      </div>
                                    )}
                                    {(journey.schedulingSettings as any)
                                      .sendOnWeekends === false && (
                                      <Badge
                                        variant="outline"
                                        className="text-xs"
                                      >
                                        Weekdays only
                                      </Badge>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400">
                                    No limits
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewAnalytics(journey)}
                                  data-testid={`button-analytics-${journey.id}`}
                                >
                                  <BarChart3 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditJourney(journey)}
                                  data-testid={`button-edit-journey-${journey.id}`}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleTestJourney(journey)}
                                  disabled={testJourneyMutation.isPending}
                                  data-testid={`button-test-${journey.id}`}
                                >
                                  <TestTube className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    toggleJourneyMutation.mutate({
                                      journeyId: journey.id,
                                      isActive: journey.status !== "active",
                                    })
                                  }
                                  disabled={toggleJourneyMutation.isPending}
                                  data-testid={`button-toggle-${journey.id}`}
                                  title={
                                    journey.status === "active"
                                      ? "Pause Journey"
                                      : "Activate Journey"
                                  }
                                >
                                  {journey.status === "active" ? (
                                    <Pause className="h-4 w-4" />
                                  ) : (
                                    <Play className="h-4 w-4" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteJourney(journey)}
                                  disabled={
                                    deleteCustomJourneyMutation.isPending
                                  }
                                  data-testid={`button-delete-journey-${journey.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Journey Pagination */}
                    {journeysPagination.totalPages > 1 && (
                      <div className="flex items-center justify-between mt-4">
                        <p className="text-sm text-gray-500">
                          Showing{" "}
                          {(journeysPagination.page - 1) *
                            journeysPagination.limit +
                            1}{" "}
                          to{" "}
                          {Math.min(
                            journeysPagination.page * journeysPagination.limit,
                            journeysPagination.total
                          )}{" "}
                          of {journeysPagination.total} journeys
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setJourneyPage((prev) => Math.max(prev - 1, 1))
                            }
                            disabled={journeysPagination.page <= 1}
                          >
                            Previous
                          </Button>
                          <span className="text-sm">
                            Page {journeysPagination.page} of{" "}
                            {journeysPagination.totalPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setJourneyPage((prev) =>
                                Math.min(
                                  prev + 1,
                                  journeysPagination.totalPages
                                )
                              )
                            }
                            disabled={
                              journeysPagination.page >=
                              journeysPagination.totalPages
                            }
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="configure" className="space-y-6">
          {!selectedJourneyForConfigure ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GitBranch className="h-5 w-5" />
                  Journey Configuration
                </CardTitle>
                <CardDescription>
                  Select a journey to configure its trigger and immediate-send
                  template.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label className="text-base font-medium">
                      Choose Journey
                    </Label>
                    <p className="text-sm text-muted-foreground mb-4">
                      Select an existing journey to configure the event trigger
                      and email template that should send instantly.
                    </p>
                  </div>

                  {journeysLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[1, 2, 3].map((i) => (
                        <Card key={i} className="animate-pulse">
                          <CardContent className="p-4">
                            <div className="h-4 bg-muted rounded mb-2"></div>
                            <div className="h-3 bg-muted rounded w-3/4 mb-2"></div>
                            <div className="h-3 bg-muted rounded w-1/2"></div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : journeys.length === 0 ? (
                    <Card>
                      <CardContent className="p-8 text-center">
                        <MapPin className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                        <h3 className="text-lg font-medium mb-2">
                          No Journeys Available
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          Create a custom journey first before configuring its
                          behaviour.
                        </p>
                        <Button
                          onClick={() => setActiveTab("journeys")}
                          data-testid="button-create-first-journey"
                        >
                          Go to Custom Journeys
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {journeys.map((journey) => (
                        <Card
                          key={journey.id}
                          className="cursor-pointer transition-colors hover:bg-muted/50"
                          onClick={() =>
                            setSelectedJourneyForConfigure(journey)
                          }
                          data-testid={`card-journey-${journey.id}`}
                        >
                          <CardContent className="p-4 space-y-3">
                            <div className="flex items-start justify-between mb-2">
                              <h3 className="font-medium truncate">
                                {journey.name}
                              </h3>
                              <Badge
                                variant={
                                  journey.status === "active"
                                    ? "default"
                                    : "secondary"
                                }
                                className="ml-2 shrink-0"
                              >
                                {journey.status
                                  ? journey.status.charAt(0).toUpperCase() +
                                    journey.status.slice(1)
                                  : "Draft"}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                              {journey.description ||
                                "No description provided yet."}
                            </p>
                            <div className="grid gap-1 text-xs text-muted-foreground">
                              <div>
                                <strong>Trigger:</strong>{" "}
                                {journey.triggerEvent || "Not set"}
                              </div>
                              <div>
                                <strong>Template:</strong>{" "}
                                {journey.sesTemplateName || "Not set"}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Journey Header */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <GitBranch className="h-6 w-6 text-primary" />
                      <div>
                        <h2 className="text-xl font-semibold">
                          {selectedJourneyForConfigure.name}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                          {selectedJourneyForConfigure.description ||
                            "No description"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={
                          selectedJourneyForConfigure.status === "active"
                            ? "default"
                            : "secondary"
                        }
                      >
                        {selectedJourneyForConfigure.status
                          ? selectedJourneyForConfigure.status
                              .charAt(0)
                              .toUpperCase() +
                            selectedJourneyForConfigure.status.slice(1)
                          : "Draft"}
                      </Badge>
                      <Button
                        variant="outline"
                        onClick={() => setSelectedJourneyForConfigure(null)}
                        data-testid="button-back-to-journey-list"
                      >
                        Back to Journey List
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Journey Configuration Component */}
              <StepConfiguration
                journeyId={selectedJourneyForConfigure.id}
                onStepUpdated={() => {
                  // Refresh journey data if needed
                  refetchJourneys();
                }}
              />
            </div>
          )}
        </TabsContent>
        {/* Testing Tab */}
        <TabsContent value="testing" className="space-y-6">
          <JourneyTesting onBack={() => setActiveTab("journeys")} />
        </TabsContent>
      </Tabs>

      {/* Create Template Dialog */}
      <Dialog
        open={isCreateTemplateDialogOpen}
        onOpenChange={setIsCreateTemplateDialogOpen}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Journey Template</DialogTitle>
            <DialogDescription>
              Create a reusable template for email journeys
            </DialogDescription>
          </DialogHeader>
          <Form {...templateForm}>
            <form
              onSubmit={templateForm.handleSubmit((data) =>
                createTemplateMutation.mutate(data)
              )}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={templateForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Template Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Welcome Series"
                          {...field}
                          data-testid="input-template-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={templateForm.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger data-testid="select-template-category">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {templateCategories.slice(1).map((category) => (
                              <SelectItem
                                key={category.value}
                                value={category.value}
                              >
                                {category.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={templateForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="A 3-step welcome journey for new users..."
                        className="min-h-[100px]"
                        {...field}
                        data-testid="textarea-template-description"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={templateForm.control}
                name="triggerEvent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trigger Event</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger data-testid="select-trigger-event">
                          <SelectValue placeholder="Select trigger event" />
                        </SelectTrigger>
                        <SelectContent>
                          {triggerEvents?.map((event: TriggerEvent) => (
                            <SelectItem key={event.id} value={event.id}>
                              <div>
                                <div className="font-medium">{event.name}</div>
                                <div className="text-xs text-gray-500">
                                  {event.description}
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">Template Configuration</h4>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={templateForm.control}
                    name="config.maxSteps"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Steps</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="1"
                            max="20"
                            {...field}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                            data-testid="input-max-steps"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={templateForm.control}
                    name="config.defaultDelay"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default Delay (hours)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            {...field}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                            data-testid="input-default-delay"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={templateForm.control}
                  name="config.priority"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Priority</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger data-testid="select-priority">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={templateForm.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Active Template
                        </FormLabel>
                        <FormDescription>
                          Allow this template to be used for creating new
                          journeys
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="switch-template-active"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateTemplateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createTemplateMutation.isPending}
                  data-testid="button-save-template"
                >
                  {createTemplateMutation.isPending
                    ? "Creating..."
                    : "Create Template"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Create / Edit Journey Dialog */}
      <Dialog
        open={isCreateJourneyDialogOpen || isEditJourneyDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateJourneyDialogOpen(false);
            setIsEditJourneyDialogOpen(false);
            setSelectedJourney(null);
            journeyForm.reset();
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isEditJourneyDialogOpen && selectedJourney
                ? "Edit Custom Journey"
                : "Create Custom Journey"}
            </DialogTitle>
            <DialogDescription>
              Create or update an immediate-send journey for your users
            </DialogDescription>
          </DialogHeader>
          <Form {...journeyForm}>
            <form
              onSubmit={journeyForm.handleSubmit((data) => {
                if (isEditJourneyDialogOpen && selectedJourney) {
                  updateJourneyMutation.mutate({
                    ...data,
                    id: selectedJourney.id,
                  });
                } else {
                  createJourneyMutation.mutate(data);
                }
              })}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={journeyForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Journey Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="New User Onboarding"
                          {...field}
                          data-testid="input-journey-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={journeyForm.control}
                  name="templateId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Template (Optional)</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value?.toString()}
                          onValueChange={(value) =>
                            field.onChange(value ? Number(value) : undefined)
                          }
                        >
                          <SelectTrigger data-testid="select-journey-template">
                            <SelectValue placeholder="Start from scratch" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="no-template">
                              No template
                            </SelectItem>
                            {templates
                              .filter((t) => t.isActive)
                              .map((template) => (
                                <SelectItem
                                  key={template.id}
                                  value={template.id.toString()}
                                >
                                  {template.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={journeyForm.control}
                  name="channel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Delivery Channel</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger data-testid="select-journey-channel">
                            <SelectValue placeholder="Select channel" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="sms">SMS</SelectItem>
                            <SelectItem value="in_app">In-App</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormDescription>
                        Choose whether this journey will send emails, SMS, or
                        in-app notifications
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={journeyForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="This journey guides new users through the onboarding process..."
                        className="min-h-[100px]"
                        {...field}
                        data-testid="textarea-journey-description"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={journeyForm.control}
                  name="triggerEvent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Trigger Event</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger data-testid="select-journey-trigger">
                            <SelectValue placeholder="Select trigger event" />
                          </SelectTrigger>
                          <SelectContent>
                            {triggerEvents?.map((event: TriggerEvent) => (
                              <SelectItem key={event.id} value={event.id}>
                                <div>
                                  <div className="font-medium">
                                    {event.name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {event.description}
                                  </div>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={journeyForm.control}
                  name="targetSegmentId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Target Segment (Optional)</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value?.toString()}
                          onValueChange={(value) =>
                            field.onChange(
                              value && value !== "all-users"
                                ? Number(value)
                                : undefined
                            )
                          }
                        >
                          <SelectTrigger data-testid="select-target-segment">
                            <SelectValue placeholder="All users" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all-users">All users</SelectItem>
                            {(segments as any[])?.map((segment: any) => (
                              <SelectItem
                                key={segment.id}
                                value={segment.id.toString()}
                              >
                                {segment.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Separator />

              <div className="space-y-4">
                {selectedJourneyChannel === "sms" ||
                selectedJourneyChannel === "in_app" ? (
                  smsTemplates.length > 0 ? (
                    <div className="space-y-4">
                      <h4 className="font-medium">SMS / In-App Template</h4>
                      <FormDescription className="text-xs">
                        Choose which SMS template this journey will use. In-app
                        journeys also use SMS templates for message content. You
                        can manage SMS templates from the SMS &amp; OTP Config
                        tab.
                      </FormDescription>
                      <FormField
                        control={journeyForm.control}
                        name="config.smsTemplateId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>SMS Template</FormLabel>
                            <FormControl>
                              <Select
                                value={
                                  field.value ? String(field.value) : undefined
                                }
                                onValueChange={(value) =>
                                  field.onChange(
                                    value ? Number(value) : undefined
                                  )
                                }
                              >
                                <SelectTrigger data-testid="select-journey-sms-template">
                                  <SelectValue placeholder="Select SMS template" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[300px]">
                                  {smsTemplates.map((template: any) => (
                                    <SelectItem
                                      key={template.id}
                                      value={String(template.id)}
                                    >
                                      <div className="flex flex-col">
                                        <span className="font-medium">
                                          {template.name}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {template.templateKey}
                                        </span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  ) : (
                    <div className="space-y-2 text-xs text-muted-foreground">
                      <h4 className="font-medium text-sm">SMS Template</h4>
                      <p>
                        No SMS templates found. Create SMS templates from the
                        SMS &amp; OTP Config tab first.
                      </p>
                    </div>
                  )
                ) : (
                  <div className="space-y-4">
                    <h4 className="font-medium">Email Template</h4>

                    <FormField
                      control={journeyForm.control}
                      name="sesTemplateName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>AWS SES Template (Optional)</FormLabel>
                          <FormControl>
                            <Select
                              value={field.value || undefined}
                              onValueChange={field.onChange}
                            >
                              <SelectTrigger data-testid="select-ses-template">
                                <SelectValue placeholder="Select an AWS SES template (optional)" />
                              </SelectTrigger>
                              <SelectContent
                                position="popper"
                                className="max-h-[300px]"
                              >
                                {sesTemplatesData?.templates?.length > 0 ? (
                                  sesTemplatesData.templates.map(
                                    (template: any) => (
                                      <SelectItem
                                        key={template.templateName}
                                        value={template.templateName}
                                      >
                                        <div className="flex flex-col">
                                          <span className="font-medium">
                                            {template.templateName}
                                          </span>
                                          {template.subject && (
                                            <span className="text-xs text-gray-500">
                                              {template.subject}
                                            </span>
                                          )}
                                        </div>
                                      </SelectItem>
                                    )
                                  )
                                ) : (
                                  <SelectItem value="no-templates" disabled>
                                    {sesTemplatesData
                                      ? "No AWS SES templates found"
                                      : "Loading templates..."}
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormDescription>
                            For email and in-app journeys, choose an AWS SES
                            template. SMS journeys use SMS templates.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                <Separator />

                <h4 className="font-medium">Journey Configuration</h4>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={journeyForm.control}
                    name="config.throttleLimit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Rate Limit (per hour)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="1"
                            {...field}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                            data-testid="input-throttle-limit"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={journeyForm.control}
                  name="config.allowWeekends"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Send on Weekends
                        </FormLabel>
                        <FormDescription>
                          Allow emails to be sent on Saturday and Sunday
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="switch-allow-weekends"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={journeyForm.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Activate Journey
                        </FormLabel>
                        <FormDescription>
                          Start this journey immediately after creation
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="switch-journey-active"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsCreateJourneyDialogOpen(false);
                    setIsEditJourneyDialogOpen(false);
                    setSelectedJourney(null);
                    journeyForm.reset();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    isEditJourneyDialogOpen
                      ? updateJourneyMutation.isPending
                      : createJourneyMutation.isPending
                  }
                  data-testid="button-save-journey"
                >
                  {isEditJourneyDialogOpen
                    ? updateJourneyMutation.isPending
                      ? "Updating..."
                      : "Update Journey"
                    : createJourneyMutation.isPending
                    ? "Creating..."
                    : "Create Journey"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Journey Analytics Dialog */}
      {selectedJourney && (
        <Dialog
          open={isAnalyticsDialogOpen}
          onOpenChange={setIsAnalyticsDialogOpen}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedJourney.name} - Analytics</DialogTitle>
              <DialogDescription>
                Performance metrics and insights for this journey
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {selectedJourney.activeUsers || 0}
                      </div>
                      <div className="text-sm text-gray-500">Active Users</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {selectedJourney.totalSent || 0}
                      </div>
                      <div className="text-sm text-gray-500">Emails Sent</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {selectedJourney.conversionRate || 0}%
                      </div>
                      <div className="text-sm text-gray-500">
                        Conversion Rate
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">
                        ${((selectedJourney.totalSent || 0) * 0.0001).toFixed(4)}
                      </div>
                      <div className="text-sm text-gray-500">
                        Total Cost
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        $0.0001/email
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Journey Details */}
              <Card>
                <CardHeader>
                  <CardTitle>Journey Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Description
                      </Label>
                      <p className="text-sm">{selectedJourney.description}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Created
                      </Label>
                      <p className="text-sm">
                        {formatDateOnlySAST(selectedJourney.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Trigger Event
                      </Label>
                      <p className="text-sm">
                        {triggerEvents?.find(
                          (e: TriggerEvent) =>
                            e.id === selectedJourney.triggerEvent
                        )?.name || selectedJourney.triggerEvent}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Target Segment
                      </Label>
                      <p className="text-sm">
                        {selectedJourney.segmentName || "All users"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={() => setIsAnalyticsDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Test Journey Dialog */}
      <Dialog
        open={isTestJourneyDialogOpen}
        onOpenChange={setIsTestJourneyDialogOpen}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Test Journey: {selectedJourneyForTest?.name}
            </DialogTitle>
            <DialogDescription>
              Enter your email address to test this journey. A test user will be
              created and the journey will be triggered.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="test-email">Test Email Address</Label>
              <Input
                id="test-email"
                type="email"
                placeholder="your-email@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                data-testid="input-test-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="test-data">Additional Test Data (JSON)</Label>
              <Textarea
                id="test-data"
                placeholder='{"customField": "value"}'
                value={JSON.stringify(testData, null, 2)}
                onChange={(e) => {
                  try {
                    setTestData(JSON.parse(e.target.value || "{}"));
                  } catch (error) {
                    // Invalid JSON, keep the text value for user to fix
                  }
                }}
                rows={3}
                data-testid="input-test-data"
              />
            </div>

            <div className="bg-blue-50 p-3 rounded-md text-sm">
              <p className="font-medium text-blue-800">Test Process:</p>
              <ol className="list-decimal list-inside text-blue-700 mt-1 space-y-1">
                <li>A test user will be created with your email</li>
                <li>The journey trigger event will be simulated</li>
                <li>
                  The journey will dispatch its email immediately using the
                  configured template
                </li>
                <li>You can monitor progress in the CRM Journeys tab</li>
              </ol>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsTestJourneyDialogOpen(false);
                setTestEmail("");
                setTestData({});
                setSelectedJourneyForTest(null); // Clear selected journey for test
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!selectedJourneyForTest || !testEmail) return;

                testJourneyMutation.mutate({
                  journeyId: selectedJourneyForTest.id,
                  testEmail,
                  testData,
                });

                setIsTestJourneyDialogOpen(false);
                setTestEmail("");
                setTestData({});
                setSelectedJourneyForTest(null); // Clear selected journey for test
              }}
              disabled={!testEmail || testJourneyMutation.isPending}
              data-testid="button-start-test"
            >
              {testJourneyMutation.isPending
                ? "Starting Test..."
                : "Start Test"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Journey Template Preview Dialog */}
      <Dialog
        open={isTemplatePreviewOpen}
        onOpenChange={setIsTemplatePreviewOpen}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Journey Template Preview</DialogTitle>
            <DialogDescription>
              Rendered view of the AWS SES template assigned to this journey.
            </DialogDescription>
          </DialogHeader>
          {templatePreviewData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-sm font-medium">Subject</Label>
                  <p className="text-sm text-gray-700">
                    {templatePreviewData.subject}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Template</Label>
                  <p className="text-sm text-gray-700">
                    {templatePreviewData.templateName ||
                      templatePreviewJourney?.sesTemplateName}
                  </p>
                </div>
              </div>

              <Tabs defaultValue="html" className="w-full">
                <TabsList>
                  <TabsTrigger value="html">HTML Preview</TabsTrigger>
                  <TabsTrigger value="text">Text Version</TabsTrigger>
                  <TabsTrigger value="code">HTML Code</TabsTrigger>
                </TabsList>

                <TabsContent value="html" className="mt-4">
                  <div className="border rounded-lg p-4 bg-white">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: sanitizeEmailContent(
                          templatePreviewData.htmlBody
                        ),
                      }}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="text" className="mt-4">
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <pre className="whitespace-pre-wrap text-sm">
                      {templatePreviewData.textBody}
                    </pre>
                  </div>
                </TabsContent>

                <TabsContent value="code" className="mt-4">
                  <div className="border rounded-lg p-4 bg-gray-900 text-green-400">
                    <pre className="text-xs overflow-x-auto">
                      <code>{templatePreviewData.htmlBody}</code>
                    </pre>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
